import { format, isFuture } from 'date-fns'
import { IncludedWithMasterClassV2Exp } from 'experiments/pen-5502_v1_CM_included_w_MC_V2'
import { Button, Nameplate as DSNameplate } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { Locations } from '@mc/track-event'
import CTA from 'components/CTA'
import Image from 'components/LegacyImage'
import { PricingText } from 'components/PricingText'
import { Course } from 'types/data'
import { TrackProps, trackAction } from '../events'
import { useGetNotifiedModal } from './GetNotifiedModalContext'
import classes from './HeroContent.module.scss'
import SampleAction, { SampleProps } from './SampleAction'
import ShareAction, { ShareProps } from './ShareAction'
import TrailerAction, { TrailerProps } from './TrailerAction'

interface ActionSectionProps {
  actionsCls?: string
  trailerVideoId: string | null | undefined
  trailerMediaUuid: string | undefined
  trackProps: TrackProps
  trailerProps?: TrailerProps
  sampleProps?: SampleProps
  shareProps?: ShareProps
}

const ActionSection = ({
  actionsCls,
  trailerVideoId,
  trailerMediaUuid,
  trackProps,
  trailerProps,
  sampleProps,
  shareProps,
}: ActionSectionProps) => (
  <div className='row justify-content-center'>
    <div className={actionsCls || 'col-auto mc-mt-4'}>
      {(trailerVideoId || trailerMediaUuid) && (
        <TrailerAction trackProps={trackProps} trailer={trailerProps} />
      )}
      <SampleAction trackProps={trackProps} sample={sampleProps} />
      <ShareAction trackProps={trackProps} share={shareProps} />
    </div>
  </div>
)

interface CTASectionProps {
  ctaCls?: string
  trackProps: TrackProps
  isComingSoon: boolean
}

const CTASection = ({ ctaCls, isComingSoon, trackProps }: CTASectionProps) => {
  const { t } = useTranslation('@mc/next')
  const { openModal } = useGetNotifiedModal()

  const finalTrackProps = {
    category: 'video-playlist-module',
    content_type: 'product',
    location: Locations.HERO,
  }

  return (
    <>
      <div className='row justify-content-center'>
        <div className={`${ctaCls || 'col-md-5 col-lg-3'} mc-py-1`}>
          {isComingSoon && (
            <Button
              className='mc-mt-4'
              kind='tertiary'
              fullWidth
              onClick={() => {
                trackAction(trackProps, t('episode.comingSoon.cta'))
                openModal()
              }}
            >
              {t('episode.comingSoon.cta')}
            </Button>
          )}
        </div>
      </div>
      <div className='row justify-content-center mc-text--center'>
        <div className={ctaCls || 'col-md-5 col-lg-3'}>
          <CTA fullWidth trackProps={finalTrackProps} />
          <p className='mc-text-small mc-opacity--hinted mc-mt-3'>
            <PricingText translationKey='long' />
          </p>
        </div>
      </div>
    </>
  )
}

interface HeroContentProps {
  showSeparator?: boolean
  namePlateCls?: string
  namePlateSize?: number
  descriptionCls?: string
  actionsCls?: string
  ctaCls?: string
  nameplate?: JSX.Element | string | null
  header?: string | null
  subheader?: string | null
  nameplateAlt?: string | null
  alternativeNameplate?: boolean
  trackProps: TrackProps
  trailerProps?: TrailerProps
  sampleProps?: SampleProps
  shareProps?: ShareProps
  releaseDate?: string
}

export const HeroContent = ({
  showSeparator = true,
  namePlateCls,
  namePlateSize = 220,
  descriptionCls,
  actionsCls,
  ctaCls,
  nameplate,
  header: headline,
  subheader: marketingCmColumn,
  nameplateAlt,
  alternativeNameplate,
  trackProps,
  trailerProps,
  sampleProps,
  shareProps,
  releaseDate,
}: HeroContentProps) => {
  const { t } = useTranslation('@mc/next')
  const { course } = useStaticData<{ course: Course }>()
  const isComingSoon = releaseDate ? isFuture(new Date(releaseDate)) : false
  const releaseDateFormatted = isComingSoon
    ? format(new Date(releaseDate as string), 'MMM yyyy')
    : ''

  const ctaSection = (
    <CTASection
      ctaCls={ctaCls}
      isComingSoon={isComingSoon}
      trackProps={trackProps}
    />
  )
  const actions = (
    <ActionSection
      actionsCls={actionsCls}
      trailerVideoId={course.brightcoveVideoId}
      trailerMediaUuid={course.mediaUuid}
      trackProps={trackProps}
      trailerProps={trailerProps}
      sampleProps={sampleProps}
      shareProps={shareProps}
    />
  )

  return (
    <div className='container'>
      <div className='mc-text--center'>
        <div className='row justify-content-center'>
          <div className='col-12'>
            {isComingSoon && (
              <p className='mc-text--bold'>
                {t('episode.comingSoon.copy', { releaseDateFormatted })}
              </p>
            )}
          </div>
          <div className={namePlateCls || 'col-8 col-sm-6 col-md-3'}>
            <>
              {alternativeNameplate ? (
                <AlternativeNameplate
                  nameplate={nameplate}
                  header={headline}
                  showSeparator={showSeparator}
                />
              ) : (
                <DefaultNameplate
                  header={headline}
                  nameplate={nameplate as string}
                  namePlateSize={namePlateSize}
                  showSeparator={showSeparator}
                  nameplateAlt={nameplateAlt}
                />
              )}
            </>
          </div>
        </div>
        <div className='row d-flex justify-content-center'>
          <div
            className={descriptionCls || 'col-12 col-md-8 col-lg-6'}
            style={{ zIndex: 1 }}
          >
            {marketingCmColumn}
          </div>
        </div>
      </div>
      {ctaSection}
      {actions}
      <IncludedWithMasterClassV2Exp />
    </div>
  )
}

const AlternativeNameplate = ({
  header: headline,
  nameplate,
  showSeparator,
}: Pick<HeroContentProps, 'header' | 'nameplate' | 'showSeparator'>) => (
  <div className='d-flex flex-column align-items-center'>
    <h1
      className={`mc-text-d1 mc-text--brand mc-text--uppercase ${classes.alternativeNameplate}`}
    >
      {headline}
    </h1>
    {showSeparator && (
      <div
        className='mc-mt-4'
        style={{
          height: '4px',
          width: '20px',
          backgroundColor: 'var(--mc-color-primary-default)',
        }}
      />
    )}
    <h2 className='mc-text-h4 mc-mt-4'>{nameplate}</h2>
  </div>
)

const DefaultNameplate = ({
  nameplate,
  header: headline = '',
  namePlateSize,
  showSeparator,
  nameplateAlt,
}: Pick<HeroContentProps, 'header' | 'namePlateSize' | 'showSeparator'> & {
  nameplate: string
  nameplateAlt?: string | null
}) => {
  if (!nameplate || !headline) return null

  return (
    <DSNameplate
      size='small'
      image={
        <h1
          className={`${classes.nameplateImageContainer} align-items-end justify-content-center`}
        >
          <Image
            alt={nameplateAlt || ''}
            className={classes.nameplateImage}
            src={nameplate}
            layout='fill'
            sizes={`${namePlateSize}px`}
            priority
          />
        </h1>
      }
      separator={showSeparator}
      subText={headline}
      className='mc-text--center'
    />
  )
}
